import React, { useEffect, useState } from "react";
import { Save, RefreshCw, Sparkles, ShieldAlert, AlertCircle } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

const MODELS = [
    { id: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5", provider: "Anthropic", active: true },
    { id: "gpt-4o", label: "GPT-4o", provider: "OpenAI" },
    { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash", provider: "Google" },
];

export default function SettingsAI() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [retraining, setRetraining] = useState(false);

    useEffect(() => {
        adminApi.get("/api/admin/settings/ai").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="p-10 text-stone-500">Loading…</div>;

    const save = async () => {
        setSaving(true);
        try {
            await adminApi.put("/api/admin/settings/ai", {
                llm_model: data.llm_model,
                base_instructions: data.base_instructions,
                temperature: data.temperature,
                auto_retrain: data.auto_retrain,
                strict_grounding: data.strict_grounding,
                fallback_message: data.fallback_message,
            });
            toast.success("AI settings saved");
        } finally {
            setSaving(false);
        }
    };

    const retrain = async () => {
        setRetraining(true);
        try {
            const r = await adminApi.post("/api/admin/settings/ai/retrain");
            setData({ ...data, last_trained_at: r.data.last_trained_at });
            toast.success("Priya retrained on latest knowledge");
        } finally {
            setRetraining(false);
        }
    };

    const tempLabel = data.temperature <= 0.3 ? "Reserved" : data.temperature <= 0.7 ? "Balanced" : "Creative";

    return (
        <div className="p-10 max-w-4xl">
            <PageHeader eyebrow="Settings" title="AI" testid="page-settings-ai" />

            {data.kb && data.kb.total === 0 && (
                <div
                    className="mb-6 border border-gold-500/40 bg-gold-500/5 rounded-lg p-4 flex items-start gap-3"
                    data-testid="kb-empty-banner"
                >
                    <AlertCircle className="text-gold-700 mt-0.5 shrink-0" size={18} />
                    <div className="flex-1 text-sm">
                        <div className="font-medium text-stone-900">
                            Your knowledge base is empty.
                        </div>
                        <div className="text-stone-600 mt-0.5">
                            Priya has nothing to ground answers on — she&apos;ll respond with the
                            fallback below for every query. Upload at least one document, Q&amp;A,
                            snippet or website page on the <strong>Data sources</strong> screens.
                        </div>
                    </div>
                </div>
            )}
            {data.kb && data.kb.total > 0 && (
                <div
                    className="mb-6 border border-sage-500/30 bg-sage-500/5 rounded-lg p-3 text-xs text-stone-700"
                    data-testid="kb-counts"
                >
                    Priya is grounded on{" "}
                    <strong>{data.kb.documents}</strong> indexed documents,{" "}
                    <strong>{data.kb.qa_pairs}</strong> Q&amp;A pairs,{" "}
                    <strong>{data.kb.snippets}</strong> snippets and{" "}
                    <strong>{data.kb.website_chunks}</strong> website chunks.
                </div>
            )}

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <h2 className="font-heading text-xl text-orchid-700 mb-1">AI model</h2>
                <p className="text-xs text-stone-500 mb-5">Pick the LLM powering Priya. Switching requires a save.</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {MODELS.map((m) => (
                        <button
                            key={m.id}
                            onClick={() => setData({ ...data, llm_model: m.id })}
                            data-testid={`model-${m.id}`}
                            className={`text-left p-4 rounded-lg border-2 transition-colors ${
                                data.llm_model === m.id
                                    ? "border-orchid-700 bg-orchid-700/5"
                                    : "border-stone-200 hover:border-stone-300"
                            }`}
                        >
                            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">{m.provider}</div>
                            <div className="font-medium text-stone-900 text-sm">{m.label}</div>
                            {m.active && data.llm_model === m.id && (
                                <div className="text-[10px] uppercase tracking-wider text-orchid-700 mt-2">Active</div>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <h2 className="font-heading text-xl text-orchid-700 mb-1">Base Instructions</h2>
                <p className="text-xs text-stone-500 mb-5">Define Priya's role, persona and rules. Used as the system prompt.</p>

                <textarea
                    rows={14}
                    value={data.base_instructions || ""}
                    onChange={(e) => setData({ ...data, base_instructions: e.target.value })}
                    data-testid="base-instructions"
                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm font-mono focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 leading-relaxed"
                />
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-1">
                    <h2 className="font-heading text-xl text-orchid-700">Temperature</h2>
                    <span className="text-xs uppercase tracking-wider text-stone-500">{tempLabel} · {data.temperature}</span>
                </div>
                <p className="text-xs text-stone-500 mb-5">Lower = more factual. Higher = more creative.</p>
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={data.temperature}
                    onChange={(e) => setData({ ...data, temperature: parseFloat(e.target.value) })}
                    data-testid="temperature-slider"
                    className="w-full accent-orchid-700"
                />
                <div className="flex justify-between text-[10px] uppercase tracking-wider text-stone-400 mt-2">
                    <span>Reserved</span>
                    <span>Balanced</span>
                    <span>Creative</span>
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <div className="flex items-start justify-between gap-4 mb-1">
                    <div className="flex-1">
                        <h2 className="font-heading text-xl text-orchid-700 flex items-center gap-2">
                            <ShieldAlert size={18} className="text-orchid-700" /> Strict knowledge mode
                        </h2>
                        <p className="text-xs text-stone-500 mt-1">
                            When ON, Priya answers <strong>only</strong> from uploaded documents, Q&amp;A
                            pairs, snippets and website pages. Anything not in your knowledge base
                            receives the fallback message below. Recommended ON for production.
                        </p>
                    </div>
                    <button
                        onClick={() =>
                            setData({ ...data, strict_grounding: !data.strict_grounding })
                        }
                        data-testid="strict-grounding-toggle"
                        aria-pressed={!!data.strict_grounding}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full shrink-0 transition-colors ${
                            data.strict_grounding ? "bg-orchid-700" : "bg-stone-300"
                        }`}
                    >
                        <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                data.strict_grounding ? "translate-x-5" : "translate-x-0.5"
                            }`}
                        />
                    </button>
                </div>

                <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">
                        Fallback message
                    </div>
                    <textarea
                        rows={3}
                        value={data.fallback_message || ""}
                        onChange={(e) => setData({ ...data, fallback_message: e.target.value })}
                        data-testid="fallback-message"
                        placeholder="What Priya says when an answer isn't in the knowledge base"
                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 leading-relaxed"
                    />
                    <p className="text-[11px] text-stone-500 mt-1">
                        Used verbatim whenever the answer isn&apos;t in your knowledge base. Include a phone
                        number or email so guests have a clear next step.
                    </p>
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <h2 className="font-heading text-xl text-orchid-700 mb-1">Training</h2>
                <p className="text-xs text-stone-500 mb-5">Last trained: {formatDateTime(data.last_trained_at) || "Never"}.</p>

                <div className="flex items-center justify-between gap-3 mb-4 pb-4 border-b border-stone-100">
                    <div>
                        <div className="text-sm font-medium text-stone-900">Auto-retrain</div>
                        <div className="text-xs text-stone-500">Priya re-indexes the knowledge base every 7 days.</div>
                    </div>
                    <button
                        onClick={() => setData({ ...data, auto_retrain: !data.auto_retrain })}
                        data-testid="auto-retrain-toggle"
                        className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                            data.auto_retrain ? "bg-orchid-700" : "bg-stone-300"
                        }`}
                    >
                        <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white ${
                                data.auto_retrain ? "translate-x-5" : "translate-x-0.5"
                            }`}
                        />
                    </button>
                </div>

                <button
                    onClick={retrain}
                    disabled={retraining}
                    data-testid="retrain-now"
                    className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-stone-200 hover:bg-stone-50 disabled:opacity-60"
                >
                    <RefreshCw size={14} className={retraining ? "animate-spin" : ""} />
                    {retraining ? "Retraining…" : "Retrain now"}
                </button>
            </div>

            <button
                onClick={save}
                disabled={saving}
                data-testid="ai-save"
                className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-5 py-2.5 rounded-md disabled:opacity-60"
            >
                <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
        </div>
    );
}
